import type { Mutation, Query, QueryClient, QueryKey } from "@tanstack/react-query";
import type { MemoryBearerToken } from "@decizyon/api-client";

interface IdentityBoundaryOptions {
  readonly bearerToken: MemoryBearerToken;
  readonly queryClient: QueryClient;
}

interface RegisteredScopeCleanup {
  readonly close: () => void;
  readonly scope: string;
}

function queryKeyIncludes(queryKey: QueryKey, value: string): boolean {
  const visit = (item: unknown): boolean => {
    if (item === value) {
      return true;
    }
    if (Array.isArray(item)) {
      return item.some(visit);
    }
    if (typeof item === "object" && item !== null) {
      return Object.values(item).some(visit);
    }
    return false;
  };
  return visit(queryKey);
}

function normalizeScope(scope: string): string {
  return scope.startsWith("capability:") ? scope.slice("capability:".length) : scope;
}

function scopesMatch(first: string, second: string): boolean {
  return normalizeScope(first) === normalizeScope(second);
}

function metadataMatchesScope(metadata: unknown, scope: string): boolean {
  if (typeof metadata === "string") {
    return scopesMatch(metadata, scope);
  }
  if (Array.isArray(metadata)) {
    return metadata.some((item) => metadataMatchesScope(item, scope));
  }
  if (typeof metadata === "object" && metadata !== null) {
    return Object.values(metadata).some((item) => metadataMatchesScope(item, scope));
  }
  return false;
}

function dataIncludesProduct(data: unknown, productId: string): boolean {
  if (Array.isArray(data)) {
    return data.some((item) => dataIncludesProduct(item, productId));
  }
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const record = data as Readonly<Record<string, unknown>>;
  if (record.productId === productId || record.id === productId) {
    return true;
  }
  return Object.values(record).some((item) => dataIncludesProduct(item, productId));
}

function queryKeyMatchesScope(queryKey: QueryKey, scope: string): boolean {
  if (scope.startsWith("product:")) {
    return queryKeyIncludes(queryKey, scope.slice("product:".length));
  }
  const normalizedScope = normalizeScope(scope);
  return queryKeyIncludes(queryKey, normalizedScope) ||
    queryKeyIncludes(queryKey, `capability:${normalizedScope}`);
}

function queryMatchesScope(query: Query, scope: string): boolean {
  return queryKeyMatchesScope(query.queryKey, scope)
    || metadataMatchesScope(query.meta, scope)
    || (scope.startsWith("product:")
      && (
        query.meta?.productScopedAggregate === true
        || dataIncludesProduct(query.state.data, scope.slice("product:".length))
      ));
}

function cleanupMatchesScope(cleanupScope: string, revokedScope: string): boolean {
  return scopesMatch(cleanupScope, revokedScope)
    || (cleanupScope === "product:*" && revokedScope.startsWith("product:"));
}

function mutationMatchesScope(mutation: Mutation, scope: string): boolean {
  const mutationKey = mutation.options.mutationKey;
  return (mutationKey !== undefined && queryKeyMatchesScope(mutationKey, scope)) || Object.values(
    mutation.options.meta ?? {},
  ).some((metadata) => metadataMatchesScope(metadata, scope));
}

export class IdentityBoundary {
  readonly #bearerToken: MemoryBearerToken;
  readonly #listeners = new Set<() => void>();
  readonly #queryClient: QueryClient;
  readonly #revokedScopes = new Set<string>();
  readonly #scopeCleanups = new Set<RegisteredScopeCleanup>();
  #snapshot: ReadonlySet<string> = new Set();

  constructor(options: IdentityBoundaryOptions) {
    this.#bearerToken = options.bearerToken;
    this.#queryClient = options.queryClient;
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  readonly getSnapshot = (): ReadonlySet<string> => this.#snapshot;

  readonly isScopeAvailable = (scope: string): boolean => (
    [...this.#revokedScopes].every((revokedScope) => !scopesMatch(revokedScope, scope))
  );

  registerSubscription(scope: string, close: () => void): () => void {
    return this.#registerScopeCleanup(scope, close);
  }

  registerProtectedOperation(scope: string, cancel: () => void): () => void {
    return this.#registerScopeCleanup(scope, cancel);
  }

  revokeScope(scope: string): void {
    const isNewRevocation = this.isScopeAvailable(scope);
    if (isNewRevocation) {
      this.#revokedScopes.add(scope);
    }
    this.#queryClient.removeQueries({
      predicate: (query) => queryMatchesScope(query, scope),
    });
    const mutationCache = this.#queryClient.getMutationCache();
    for (const mutation of mutationCache.getAll()) {
      if (mutationMatchesScope(mutation, scope)) {
        mutationCache.remove(mutation);
      }
    }
    for (const cleanup of [...this.#scopeCleanups]) {
      if (cleanupMatchesScope(cleanup.scope, scope)) {
        cleanup.close();
        this.#scopeCleanups.delete(cleanup);
      }
    }
    if (isNewRevocation) {
      this.#snapshot = new Set(this.#revokedScopes);
      this.#notify();
    }
  }

  clearIdentity(): void {
    this.#bearerToken.clear();
    this.#queryClient.clear();
    for (const cleanup of this.#scopeCleanups) {
      cleanup.close();
    }
    this.#scopeCleanups.clear();
    if (this.#revokedScopes.size > 0) {
      this.#revokedScopes.clear();
      this.#snapshot = new Set();
      this.#notify();
    }
  }

  #registerScopeCleanup(scope: string, close: () => void): () => void {
    if (!this.isScopeAvailable(scope)) {
      close();
      return () => undefined;
    }
    const cleanup = { scope, close };
    this.#scopeCleanups.add(cleanup);
    return () => this.#scopeCleanups.delete(cleanup);
  }

  #notify(): void {
    for (const listener of this.#listeners) {
      listener();
    }
  }
}
